/**
 * ErrorState — shown when an error occurs. Replaces ad-hoc error markup (.ai-error,
 * .banner-error, .sandbox-raw-error patterns). Two variants: 'banner' for boxed blocks,
 * 'inline' for compact text rows. Always shows an error icon and message. Optionally
 * renders Retry and/or Dismiss buttons.
 */
import { Icon } from './Icon.js';

export interface ErrorStateProps {
  message: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  /**
   * 'banner' = boxed block (replaces .banner-error). 'inline' = compact text row
   * (replaces .ai-error / .sandbox-raw-error / .ask-bubble--error). Default 'banner'.
   */
  variant?: 'banner' | 'inline';
  /**
   * Skip the wrapping `role="alert"` element and render just the icon/message/
   * action content. For embedding inside a caller-owned alert container (e.g. a
   * chat bubble that already carries its own `role="alert"`) so there's no
   * nested-alert semantics, while still sharing this component's markup instead
   * of hand-duplicating it at the call site.
   */
  bare?: boolean;
}

function ErrorStateContent({
  message,
  onRetry,
  onDismiss,
  iconSize,
}: {
  message: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  iconSize: 'sm' | 'xs';
}): React.JSX.Element {
  return (
    <>
      <Icon name="error" size={iconSize} />
      <span className="error-state-message">{message}</span>
      {onRetry && (
        <button type="button" className="btn btn-ghost" onClick={onRetry}>
          Retry
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onDismiss}
          aria-label="Dismiss error"
        >
          ×
        </button>
      )}
    </>
  );
}

export function ErrorState({
  message,
  onRetry,
  onDismiss,
  variant = 'banner',
  bare = false,
}: ErrorStateProps): React.JSX.Element {
  const iconSize = variant === 'banner' ? 'sm' : 'xs';

  if (bare) {
    return <ErrorStateContent message={message} onRetry={onRetry} onDismiss={onDismiss} iconSize={iconSize} />;
  }

  return (
    <div role="alert" className={`error-state error-state--${variant}`}>
      <ErrorStateContent message={message} onRetry={onRetry} onDismiss={onDismiss} iconSize={iconSize} />
    </div>
  );
}
